import { useRef } from 'react';
import { Link } from 'react-router';

import Text from '@cartwhl/ui/base/TextNew';

import type { DashboardAppletCardData } from '../types';

export interface DashboardAppletCardProps {
  applet: DashboardAppletCardData;
}

export default function DashboardAppletCard({
  applet,
}: DashboardAppletCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardClassName = `group flex min-h-[116px] gap-5 overflow-hidden rounded-2xl border border-primary bg-primary p-2 pr-4 transition-colors ${
    applet.disabled ? 'opacity-60' : 'hover:bg-secondary'
  }`;

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    void video.play().catch(() => undefined);
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
  };

  const cardInteractionProps = applet.videoSrc
    ? {
        onBlur: stopPreview,
        onFocus: playPreview,
        onMouseEnter: playPreview,
        onMouseLeave: stopPreview,
      }
    : undefined;
  const content = (
    <>
      <div className="bg-tertiary h-[100px] w-[100px] flex-none overflow-hidden rounded-lg">
        {applet.videoSrc ? (
          <video
            ref={videoRef}
            src={applet.videoSrc}
            poster={applet.imageSrc}
            className="h-full w-full object-cover"
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
          />
        ) : applet.imageSrc ? (
          <img
            src={applet.imageSrc}
            alt={applet.imageAlt ?? ''}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Text token="14/medium" className="text-primary flex-1 truncate">
            {applet.title}
          </Text>
          {applet.badge ? (
            <span className="ml-auto flex-none rounded-full bg-black/8 px-2 py-1">
              <Text token="12/medium" className="text-secondary">
                {applet.badge}
              </Text>
            </span>
          ) : null}
        </div>

        {applet.description ? (
          <Text token="14/medium" className="text-tertiary line-clamp-3">
            {applet.description}
          </Text>
        ) : null}
      </div>
    </>
  );

  if (applet.disabled) {
    return (
      <div
        aria-disabled="true"
        className={cardClassName}
        {...cardInteractionProps}
      >
        {content}
      </div>
    );
  }

  if (applet.external) {
    return (
      <a href={applet.path} className={cardClassName} {...cardInteractionProps}>
        {content}
      </a>
    );
  }

  return (
    <Link to={applet.path} className={cardClassName} {...cardInteractionProps}>
      {content}
    </Link>
  );
}
